// ═══════════════════════════════════════════════════════════════
//   SSO Handoff (V19.7) — one-shot token from Employee Portal
// ═══════════════════════════════════════════════════════════════
//
// The Employee Portal tile launches a URL like:
//   https://clientfinancials.steelemartin.com/sso?token=<URL_ENCODED_JWT>
//
// This page:
//   1. Reads ?token=... from the URL.
//   2. URL-decodes it.
//   3. Calls /api/auth/me to validate the token + fetch the user payload.
//   4. Persists token + user via setAuthData (and the bare 'token' key for
//      cross-app convention).
//   5. Strips ?token from the URL.
//   6. Redirects into the app (Home → role router).
//
// If the token is missing or invalid:
//   - In SSO mode (REACT_APP_PORTAL_URL set), bounces to the portal login.
//   - In local/dev mode, sends the user to /login.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { setAuthData } from '../lib/utils';
import { Loader2, AlertTriangle } from 'lucide-react';

const PORTAL_URL = process.env.REACT_APP_PORTAL_URL;

function SSOFailure({ message }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', background: '#0F172A', color: '#F1F5F9',
      fontFamily: 'Inter, system-ui, sans-serif', padding: 24,
    }}>
      <AlertTriangle size={36} color="#F59E0B" />
      <div style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 20, marginTop: 16 }}>
        Sign-in handoff failed
      </div>
      <div style={{ color: '#CBD5E1', marginTop: 8, fontSize: 13, maxWidth: 420, textAlign: 'center' }}>
        {message || 'We couldn\'t validate your session token.'}
      </div>
      {PORTAL_URL && (
        <a
          data-testid="sso-back-to-portal"
          href={`${PORTAL_URL}/login`}
          style={{
            marginTop: 24, padding: '10px 18px', background: '#22D3EE', color: '#0F172A',
            borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: 'none',
          }}
        >
          Return to Employee Portal
        </a>
      )}
    </div>
  );
}

export default function SSOPage() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) {
      // No token in URL — fall back to login flow.
      if (PORTAL_URL) {
        window.location.href = `${PORTAL_URL}/login`;
      } else {
        navigate('/login', { replace: true });
      }
      return;
    }

    // V19.9 — Trust-on-handoff: decode the JWT client-side and persist
    // immediately. We DO NOT call /api/auth/me here.
    //
    // Why: the previous design called /api/auth/me on every handoff to
    // validate the token + fetch the user payload. That cross-origin
    // call had to thread the needle of Emergent's edge proxy + browser
    // CORS rules, and broke on virtually every redeploy due to subtle
    // changes in how CORS headers are emitted. The result was a brittle
    // SSO flow that needed repeated babysitting.
    //
    // Instead, we now:
    //   1. Decode the JWT locally (no network, no CORS surface area).
    //   2. Persist the token + a stub user record from the JWT claims.
    //   3. Hard-redirect into the app. The next authenticated API call
    //      (typically /api/clients on the dashboard) will validate the
    //      token via the same backend Depends(get_current_user) — and
    //      that already-existing API surface owns the rejection path
    //      if the token is bad. AuthContext will reconcile the user
    //      record from /api/auth/me on its normal init pass once the
    //      app boots.
    //
    // If the JWT is genuinely malformed, we still show a friendly error.
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Token is not a valid JWT (expected 3 segments)');
      }
      // base64url-decode the payload. atob handles base64 standard
      // alphabet; we add padding + swap URL-safe chars.
      const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
        .padEnd(parts[1].length + (4 - parts[1].length % 4) % 4, '=');
      const claims = JSON.parse(decodeURIComponent(escape(atob(b64))));

      // Sanity checks. We're not validating the signature here (only the
      // backend has the secret) — just shape-checking so we don't push
      // garbage into AuthContext.
      if (!claims.email || !claims.user_id) {
        throw new Error('JWT missing required claims (email, user_id)');
      }
      if (claims.exp && Number(claims.exp) * 1000 < Date.now()) {
        throw new Error('JWT has expired — please sign in again from the Employee Portal.');
      }

      // Build a stub user record from JWT claims. AuthContext will
      // refresh this from /api/auth/me on next mount when network is
      // healthy; if that call fails (CORS bug, network blip), we still
      // have something to show in the UI from the JWT itself.
      const portalRole = String(claims.role || 'employee').toLowerCase();
      const appRole = portalRole === 'admin' ? 'admin' : 'staff';
      const stubUser = {
        id: claims.user_id,
        email: claims.email,
        name: claims.name || claims.email.split('@')[0],
        role: appRole,
      };
      setAuthData(token, stubUser);
      try { localStorage.setItem('token', token); } catch (e) { /* ignore */ }
      try { window.history.replaceState({}, '', '/'); } catch (e) { /* ignore */ }
      // Use replace, not href, so /sso isn't in browser history.
      window.location.replace('/');
    } catch (err) {
      setError(err.message || 'Token decode failed');
    }
  }, [navigate]);

  if (error) {
    return <SSOFailure message={error} />;
  }
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', background: '#0F172A', color: '#F1F5F9',
      fontFamily: 'Inter, system-ui, sans-serif',
    }}>
      <Loader2 size={28} className="animate-spin" color="#22D3EE" />
      <div data-testid="sso-handing-off" style={{ marginTop: 16, fontSize: 13, color: '#CBD5E1' }}>
        Signing you in…
      </div>
    </div>
  );
}
